import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
// Page Components
import Greetings from './components/Greetings/Greetings';
import CurrentBalance from 'components/CurrentBalance/CurrentBalance';
import OptionsCards from './components/OptionsCards/OptionsCards';
import NewOffer from './components/NewOffer/NewOffer';
import PersonalisedPaymentPlan from './components/PersonalisedPaymentPlan/PersonalisedPaymentPlan';
import AccountDetails from 'components/AccountDetails/AccountDetails';
import PlanDetails from 'components/PlanDetails/PlanDetails';
import Detail from 'components/PlanDetails/Detail';
import EditPaymentMethod from 'components/PlanDetails/EditPaymentMethod';
import PaymentProgress from './components/PaymentProgress/PaymentProgress';
import CompleteAndUpdate from './components/CompleteAndUpdate/CompleteAndUpdate';
import HighlightedBox, {
  THEME,
} from './components/HighlightedBox/HighlightedBox';
import AccountSelect, {
  IAccountOption,
} from 'components/AccountSelect/AccountSelect';
import PayerReviewWithOffer from './components/PayerReview/PayerReviewWithOffer';
import PayerReviewPanel from './components/PayerReview/PayerReviewPanel';
import PayerReviewThankYouPanel from './components/PayerReview/PayerReviewThankYouPanel';
import PayerReviewWthoutOffer from './components/PayerReview/PayerReviewWthoutOffer';
// Const and utils
import { CustomerStatus } from 'common/consts/customerStatus';
import { PayerReview } from 'common/consts/payerReview';
import { isNullOrWhiteSpace } from 'common/helpers/string';
import { getPathnameFromUrl } from 'common/helpers/resolveHost';
// Page styles
import styles from './DebtOverviewPageData.module.scss';
import ManualPaymentBanner from './components/ManualPaymentBanner/ManualPaymentBanner';
import { ManualPaymentBannerType } from 'common/consts/manualPaymentBanner';
import NoPaymentProgress from './components/NoPaymentProgress/NoPaymentProgress';



interface Props {
  PageData?: any;
  Model?: any;
  LayoutModel?: any;
}

const MARKET_SPAIN = 'Spain';

const DebtOverviewPageData = (props: Props) => {
  const history = useHistory();
  const { Model, PageData, LayoutModel } = props;
  const [
    payerReviewRedirectToThankYouState,
    setpayerReviewRedirectToThankYouState,
  ] = useState(Model?.payerReviewViewModel.RedirectToPayerThankYou);  
  const hasMultipleProfiles = (LayoutModel?.Profiles || []).length > 1;
  const accountsList: IAccountOption[] = useMemo(
    () =>
      (LayoutModel?.Profiles || []).map((acc: any, index: number) => ({
        value: Model?.ProfileUrls[index],
        reference:
          acc.Market === MARKET_SPAIN
            ? acc.DebtReference
            : acc.ReferenceToDisplay,
        balance:
          acc.Status != CustomerStatus.LegalProcess && !acc.IsClosed
            ? acc.FormattedCurrency
            : undefined,
        profileId: acc.ProfileId,
      })),
    [LayoutModel?.Profiles, Model?.ProfileUrls],
  );

  const onAccountChange = (profileUrl: string) => {
    var nextPagePath = getPathnameFromUrl(profileUrl);
    history.push(nextPagePath, {
      noRefresh: false,
    });
  };
    
  return (
    <div className={styles.wrapper} id={styles.test}>
      <div className={styles.section}>
        <Greetings
          greeting={PageData?.Greeting}
          username={Model?.UserFullName}
          hasMultipleAccounts={hasMultipleProfiles}
          multipleAccountLabel={PageData?.MyaccountsLabel}
        />
      </div>
      
      {Model.ShowPayerReviewOfferPanel === true &&
        Model.payerReviewViewModel.RedirectToPayerThankYou === false && (
          <div className={styles.section}>
            <PayerReviewWithOffer {...props} />
          </div>
        )}
      {Model.ShowPayerReviewPanel === true &&
        Model.payerReviewViewModel.PayerReview === PayerReview.ReviewWithIE &&
        Model.payerReviewViewModel.RedirectToPayerThankYou === false && (
          <div className={styles.section}>
            <PayerReviewPanel {...props} />
          </div>
        )}
      {Model.payerReviewViewModel !== null &&
        payerReviewRedirectToThankYouState && (
          <div className={styles.section}>
            <PayerReviewThankYouPanel
              onClose={() => {
                setpayerReviewRedirectToThankYouState(false);
              }}
              {...props}
            />
          </div>
        )}
      {Model.ShowPayerReviewWithoutOfferPanel === true &&
        Model.payerReviewViewModel.PayerReview ===
          PayerReview.ReviewWithoutOffer &&
        Model.payerReviewViewModel.RedirectToPayerThankYou === false && (
          <div className={styles.section}>
            <PayerReviewWthoutOffer {...props} />
          </div>
        )}

      {hasMultipleProfiles &&
        LayoutModel?.Settings?.EnableMultiAccountPortal &&
        ((LayoutModel?.Profiles !== null &&
          LayoutModel?.Profiles?.some(
            (x: any) => x.Status !== CustomerStatus.AccountClosed,
          )) ||
          Model.IsCmsEditMode) && (
          <div className={styles.section}>
            <AccountSelect
              optionsList={accountsList}
              selectedOption={LayoutModel?.ProfileId}
              onChange={onAccountChange}
            />
          </div>
        )}

      {Model?.CustomerStatus != CustomerStatus.AccountClosed &&
        Model.DisplayManualPaymentBanner != null &&
        Model.DisplayManualPaymentBanner != ManualPaymentBannerType.None && (
          <div className={styles.section}>
            <ManualPaymentBanner {...props} />
          </div>
        )}
      {Model?.CustomerStatus === CustomerStatus.LegalProcess &&
        !isNullOrWhiteSpace(
          Model.DebtOverviewPanelViewModel.CustomerInLegalProcessMessage,
        ) && (
          <div className={styles.section}>
            <div
              data-type="Alert"
              data-creative-slot="Portal-Home-Top"
              data-alert-name="Legal"
            >
              <HighlightedBox theme={THEME.BLUE}>
                <>{PageData?.CustomerInLegalProcessMessage}</>
              </HighlightedBox>
            </div>
          </div>
        )}
      {Model.CustomerStatus == CustomerStatus.AccountClosed && (
        <div className={styles.section}>
          <div
            data-type="Alert"
            data-creative-slot="Portal-Home-Top"
            data-alert-name="Account closed"
          >
            <HighlightedBox theme={THEME.BLUE}>
              {PageData?.AccountClosedMessage}
            </HighlightedBox>
          </div>
        </div>
      )}
      {Model.CustomerStatus === CustomerStatus.ClosingInProgress && (
        <div className={styles.section}>
          <HighlightedBox theme={THEME.BLUE}>
            <>{PageData?.AccountClosingInProgressMessage}</>
          </HighlightedBox>
        </div>
      )}
      {(Model.IsCurrentBalanceVisible ||
        Model.IsCmsEditMode ||
        (Model.PaymentPlan !== null &&
          Model.CustomerStatus !== CustomerStatus.ClosingInProgress)) && (
        <div className={styles.section}>
          <CurrentBalance
            heading={PageData?.MyCurrrentBalanceHeading}
            currency={Model?.Balance?.Currency}
            balance={(Model?.showPlanCurrentBalance) ? Model?.PaymentPlan?.CurrentBalanceAmountText : Model?.FormattedCurrencyDebtBalance}
            balanceTooltip={PageData?.MyCurrentBalanceTooltip}
            noPaymentPlan={
              Model?.PaymentPlan?.NoPaymentPlan || Model?.IsCmsEditMode
            }
            noPaymentPlanHeading={PageData?.NoPaymentplanHeading}
            noPaymentPlanTooltip={PageData?.NoPaymentplanTooltip}     
            ShowCurrentBalanceEmpty = {Model?.showPlanCurrentBalance && Model?.IsNewPayment}       
          >
            <>
              {((Model.HasPlanAccessPermission &&
                !Model.PaymentPlan.NoPaymentPlan) ||
                Model.IsCmsEditMode) &&
                (Model.CustomerStatus !== CustomerStatus.LegalProcess ||
                  Model.IsCmsEditMode) && <PaymentProgress {...props} />}
              {((Model.PaymentPlan != null &&
                Model.PaymentPlan.NoPaymentPlan) ||
                Model.PaymentPlan == null) &&
                (Model.CustomerStatus !== CustomerStatus.LegalProcess ||
                  Model.IsCmsEditMode) && <NoPaymentProgress {...props} />}
            </>
          </CurrentBalance>
        </div>
      )}
      {Model.CustomerStatus !== CustomerStatus.AccountClosed &&
        !Model.HideCardsExceptPayerReview && (
          <div className={styles.section}>
            <NewOffer {...props} />
          </div>
        )}
      {Model.ProposedPaymentPlanTerm !== null &&
        Model.ProposedPaymentPlanInstallmentAmount !== null &&
        Model.ShowProposedPlan &&
        !Model.HasOffer && (
          <div className={styles.section}>
            <PersonalisedPaymentPlan {...props} />
          </div>
        )}
      <div className={styles.section}></div>
      {Model.CustomerStatus !== CustomerStatus.AccountClosed && (
        <>
          {Model.PaymentPlan !== null && !Model.PaymentPlan.NoPaymentPlan && (
            <div className={styles.section}>
              <PlanDetails
                heading={PageData?.RepaymentPlanDetailsHeading}
                headingOnTheLeft={true}
                numberOfColumns={4}
                showPlanDetailsLabel={PageData?.ShowPlanDetailsLabel}
                hidePlanDetailsLabel={PageData?.HidePlanDetailsLabel}
              >
                <Detail
                  label={PageData?.RecentPaymentLabel}
                  value={Model?.PaymentPlan?.LastPaymentAmountText}
                  isHighlighted
                  isHtml
                />
                <Detail
                  label={PageData?.NextPaymentDateLabel}
                  value={Model?.PaymentPlan?.NextPaymentDateText}
                />
                <Detail
                  label={PageData?.RecentPaymentDateLabel}
                  value={Model?.PaymentPlan?.LastPaymentDateText}
                  isHtml
                />
                <Detail
                  label={PageData?.AgreedAmountLabel}
                  value={Model?.PaymentPlan?.AgreedInstalmentAmountText}
                  isHtml
                />
                <Detail
                  label={PageData?.PaymentFrequencyLabel}
                  value={Model?.PaymentPlan?.PaymentFrequencyText}
                />
                {
                  !Model.PaymentPlan.HidePaymentEndDateForArrearUser && (
                  <>
                  <Detail
                    label={PageData?.PaymentsRemainingLabel}
                    value={Model?.PaymentPlan?.InstalmentsRemainingText}
                  /> 
                  <Detail
                    label={PageData?.PaymentsEndDateLabel}
                    value={Model?.PaymentPlan?.PlanEndDateText}
                  />
                  </>                 
                  )
                }                
                {Model.PaymentPlan.FinalPaymentAmount &&
                  Model.PaymentPlan.FinalPaymentAmount > 0 && (
                    <Detail
                      label={PageData?.FinalPaymentLabel}
                      value={Model?.PaymentPlan?.FinalPaymentAmountText}
                      isHtml
                    />
                  )}
                {Model.PaymentDetailsViewModel != null &&
                  Model.PaymentDetailsViewModel.BankSortCode && (
                    <Detail
                      label={PageData?.BankSortCodeLabel}
                      value={Model?.PaymentDetailsViewModel.BankSortCode}
                    />
                  )}
                {Model.PaymentDetailsViewModel != null &&
                  Model.PaymentDetailsViewModel.BankAccountNumber && (
                    <Detail
                      label={PageData?.BankAccountLabel}
                      value={Model?.PaymentDetailsViewModel.BankAccountNumber}
                    />
                  )}
                <EditPaymentMethod
                  label={PageData?.PaymentMethodLabel}
                  type={Model?.PaymentPlan?.PaymentMethodText}
                  icon={Model?.PaymentMethodType}
                  buttonUrl={Model?.DebtOverviewPanelViewModel?.EditLinkUrl}
                  buttonText={PageData?.EditLabel}
                  disabled={!Model?.HasRightsToEditPaymentMethod}
                />
              </PlanDetails>
            </div>
          )}

          <div className={styles.section}>
            <OptionsCards {...props} />
          </div>
          {((Model?.ContactDetails?.HasDataCompletenessPercentValue &&
            Model?.ContactDetails?.DataCompletenessPercent < 100) ||
            Model?.IsCmsEditMode) && (
            <div className={styles.section}>
              <CompleteAndUpdate
                mainText={PageData?.DataCompletenessBeginningLabel}
                percents={Model?.ContactDetails.DataCompletenessPercent}
                highlightedText={PageData?.DataCompletenessEndingLabel}
                buttonText={PageData?.DataCompletenessButtonLabel}
                buttonUrl={Model?.CustomerProfilePageUrl}
              />
            </div>
          )}
          <div className={styles.section}>
            <AccountDetails {...props} />
          </div>
        </>
      )}
    </div>
  );
};

export default DebtOverviewPageData;
